import { ThemedCard } from '@/app/platform/core/layout/themes/components/ThemedCard.tsx';
import { ThemedLoadingSpinner } from '@/app/platform/core/layout/themes/components/ThemedLoadingSpinner.tsx';
import { ThemedText } from '@/app/platform/core/layout/themes/components/ThemedText.tsx';
import { TribeCard } from '@/app/features/tribes-projects/tribes/TribeCard.tsx';
import { AppLayout } from '@/app/platform/core/layout/AppLayout.tsx';
import { ThemeProvider } from '@/app/platform/core/layout/themes/ThemeContext.tsx';
import { useCurrentUserProfile } from '@/app/platform/functions/people/users/useCurrentUserProfile.ts';
import { useUserTribes } from '@/app/features/tribes-projects/tribes/useTribes.ts';
import { authorizationHooks } from '@/app/platform/core/authorization/authorization-hooks.ts';
import { errorStyle } from '@/app/platform/core/layout/themes/theme.styles.tsx';
import { MenuAction } from '@/app/platform/core/layout/menu.types.ts';
import { TribeEntry } from '@/app/features/tribes-projects/tribes/tribes.query.types.ts';

import { BookmarkToggle } from '@/app/features/bookmarks/BookmarkToggle.tsx';
import { buildBookmarkDescription } from '@/app/features/bookmarks/types.ts';

import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

const TribesPageContent: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isLoading: currentUserLoading } = useCurrentUserProfile();
  const { tribes, loading: tribesLoading } = useUserTribes(user?.id || '', {
    enabled: !!user?.id,
  });
  const {
    data: authorization,
    error: authorizationError,
    verifyAuthorization,
  } = authorizationHooks();

  const dedupedTribes = useMemo((): TribeEntry[] => {
    const map = new Map<string, TribeEntry>();
    for (const row of tribes) {
      const existing = map.get(row.tribe_id);
      if (!existing) {
        map.set(row.tribe_id, {
          tribe_id: row.tribe_id,
          tribe_url_param_id: row.tribe_url_param_id,
          tribe_name: row.tribe_name,
          direct_position: row.via_represents ? null : row.position,
          represented_persons: row.via_represents
            ? [
                {
                  first_name: row.person_first_name,
                  last_name: row.person_last_name,
                  position: row.position,
                },
              ]
            : [],
        });
      } else {
        if (!row.via_represents) {
          existing.direct_position = row.position;
        } else {
          existing.represented_persons.push({
            first_name: row.person_first_name,
            last_name: row.person_last_name,
            position: row.position,
          });
        }
      }
    }
    return Array.from(map.values());
  }, [tribes]);

  const breadcrumbs = [
    { label: t('common.home'), path: '/app' },
    { label: t('tribes.title') },
  ];

  // Check authorization
  useEffect(() => {
    verifyAuthorization(['admin', 'can_create_own_tribes']).catch((err) => {
      console.error('Authorization check failed:', err);
    });
  }, [verifyAuthorization]);

  const isAdmin = user?.permissions?.includes('admin') ?? false;
  const menuActions = useMemo(
    (): MenuAction[] => [
      ...(authorization?.authorized
        ? [
            {
              id: 'tribes.create',
              icon: 'plus' as const,
              badgeIcon: 'users' as const,
              label: t('tribes.createTribe'),
              path: '/app/tribes/create',
            },
          ]
        : []),
      ...(isAdmin
        ? [
            {
              id: 'tribes.admin',
              icon: 'shield' as const,
              label: t('common.admin'),
              path: '/admin',
            },
          ]
        : []),
    ],
    [authorization?.authorized, isAdmin, t],
  );

  if (currentUserLoading || tribesLoading) {
    return (
      <AppLayout menuActions={menuActions}>
        <ThemedLoadingSpinner />
      </AppLayout>
    );
  }

  return (
    <AppLayout
      menuActions={menuActions}
      breadcrumbs={breadcrumbs}
      bookmarkSlot={<BookmarkToggle pagePath={location.pathname} pageTitle={t('tribes.title')} pageDescription={buildBookmarkDescription(breadcrumbs)} />}
    >
      {authorizationError && (
        <ThemedCard>
          <div style={errorStyle}>
            <strong>Authorization Error:</strong> {authorizationError.message}
          </div>
        </ThemedCard>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {dedupedTribes.map((tribe) => (
          <TribeCard
            key={tribe.tribe_id}
            tribe={tribe}
            onClick={(t) => navigate(`/app/tribes/${t.tribe_url_param_id}`)}
          />
        ))}
      </div>

      {dedupedTribes.length === 0 && (
        <ThemedCard variant="secondary">
          <ThemedText variant="secondary" size="medium">
            {t('tribes.empty')}
          </ThemedText>
        </ThemedCard>
      )}
    </AppLayout>
  );
};

export const TribesPage: React.FC = () => {
  return (
    <ThemeProvider defaultTheme="default">
      <TribesPageContent />
    </ThemeProvider>
  );
};
